<script setup lang="ts">
//#region imports
import { cloneDeepWith } from 'lodash-es';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { isErrorResponse, type EmitsToListeners, type FormErrors } from '@/types/core';
import type {
  GetTeamsResponse,
  PostTeamsData,
} from '@/types/openapi-ts';
//#endregion

//#region types
type TeamItem = GetTeamsResponse['payload'][number];
type FormType = PostTeamsData['body'];

interface Props {
  teamId?: string;
  teamName?: string;
}

interface Emits {
  close: [];
  success: [payload: TeamItem];
}

export interface TeamParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  teamId: '',
  teamName: '',
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<FormErrors>({});
const form = ref<FormType>({
  name: '',
});
//#endregion

//#region computed
const isEdit = computed(() => Boolean(props.teamId));

const popupTitle = computed(() => (
  isEdit.value
    ? t('team_popup.edit_team_button')
    : t('team_popup.add_team_button')
));

const saveButtonLabel = computed(() => (
  isEdit.value
    ? t('team_popup.save_button')
    : t('team_popup.add_team_button')
));
//#endregion

//#region methods
function setErrors(error: unknown) {
  if (!isErrorResponse(error)) {
    throw error;
  }

  errors.value = cloneDeepWith(error.data.errors) ?? {};
}

function open() {
  form.value = {
    name: props.teamName,
  };
  errors.value = {};
  isVisible.value = true;
}

function handleClose() {
  isVisible.value = false;
  emit('close');
}

function handleSave() {
  isLoading.value = true;
  errors.value = {};

  const request = isEdit.value
    ? api.putTeam(props.teamId, form.value.name)
    : api.postTeam(form.value.name);

  request.then((response) => {
    emit('success', response.data.payload);
    handleClose();
  }).catch(setErrors).finally(() => {
    isLoading.value = false;
  });
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="popupTitle"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <UiInput
        v-model="form.name"
        :error="errors.name"
        :placeholder="t('team_popup.team_name_placeholder')"
        :label="t('team_popup.team_name_label')"
        size="big"
        autosize
      />
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ t('team_popup.cancel_button') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          :icon="isEdit ? 'check' : 'plus'"
          @click="handleSave"
        >
          {{ saveButtonLabel }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
